import { providerKindLabel } from '../../utils/lookingFor'
import { BadgeCheck, MapPin, Send, Sparkles, Trophy, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { apiErrorMessage } from '../../api/client'
import { profileApi } from '../../api/endpoints'
import { NEED_CATEGORY_LABELS, summarizeJson } from '../../utils/taxonomy'
import { ErrorMessage } from '../ErrorMessage'
import { Stars } from '../profile/RecentRatings'
import { ReportBlockActions } from '../trust/ReportBlockActions'
import { TrustScoreTile } from '../trust/TrustScoreTile'
import type { MapProviderResponse, ParticipantCardResponse } from '../../types/api'

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
}

function modeLabel(onlineOnly: boolean | null | undefined): string {
  if (onlineOnly === true) return 'Online only'
  if (onlineOnly === false) return 'In person (and possibly online)'
  return 'Not stated'
}

/** The full provider profile a learner sees before deciding to send a request. */
export function ProviderDialog({
  provider,
  connecting,
  onClose,
  onConnect,
  /** Null when Connect should be offered; a reason to show instead when it should not. */
  connectBlockedReason,
  /** "Connected" / "Request sent" / "Waiting for parent approval", when one already exists. */
  existingLabel,
  connectError,
}: {
  provider: MapProviderResponse
  connecting: boolean
  onClose: () => void
  onConnect: (card: ParticipantCardResponse | null) => void
  connectBlockedReason?: string | null
  existingLabel?: string | null
  /** Why the last Connect failed, in words the person can act on. */
  connectError?: string | null
}) {
  const [card, setCard] = useState<ParticipantCardResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    profileApi
      .getCard(provider.id)
      .then((c) => {
        if (!cancelled) setCard(c)
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Could not load this profile'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [provider.id])

  const isVerified = provider.verificationStatus === 'VERIFIED'

  return (
    <div className="fixed inset-0 z-[1100] flex items-end justify-center overflow-y-auto bg-ink-900/50 p-0 sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={provider.fullName}
        className="card flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-b-none sm:rounded-b-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-ink-200 p-4">
          <div className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden="true"
              className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-sun-400/25 text-base font-extrabold text-ink-900"
            >
              {initials(provider.fullName)}
            </span>
            <div className="min-w-0">
              <div className="truncate text-lg font-bold leading-tight text-ink-900">{provider.fullName}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-ink-500">
                <span>{providerKindLabel(provider.entityKind)}</span>
                <span className="inline-flex items-center gap-0.5">
                  · <MapPin size={11} /> ~{provider.distanceKm} km
                </span>
                {isVerified && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                    <BadgeCheck size={10} /> Verified · {provider.verificationTier}
                  </span>
                )}
                {/* Same two credentials the map pin's own tooltip already shows (see ProviderMap) -
                    missing here before, so opening the full profile silently dropped them. */}
                {provider.fourCScored && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                    <Trophy size={10} /> 4C Assessment taken
                  </span>
                )}
                {provider.aiCertified && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                    <Sparkles size={10} /> AI certified · Violet Dots
                  </span>
                )}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="btn-ghost !px-2 !py-2">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-sm text-ink-500">Loading profile...</p>
          ) : (
            <div className="space-y-4">
              <ErrorMessage message={error} />

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-ink-200 px-2 py-2">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-ink-400">Rating</div>
                  <div className="text-lg font-extrabold text-ink-900">
                    {provider.averageRating != null ? provider.averageRating.toFixed(1) : 'New'}
                  </div>
                  <div className="truncate text-[11px] text-ink-500">
                    {provider.ratingCount ? `${provider.ratingCount} rating${provider.ratingCount === 1 ? '' : 's'}` : 'no ratings yet'}
                  </div>
                </div>
                <TrustScoreTile score={card?.trustScore} breakdown={card?.trustBreakdown} />
                <div className="rounded-xl border border-ink-200 px-2 py-2">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-ink-400">Connections</div>
                  <div className="text-lg font-extrabold text-ink-900">{card?.connectionCount ?? 0}</div>
                  <div className="truncate text-[11px] text-ink-500">on YellowDots</div>
                </div>
              </div>

              {card && card.vouchedBy.length > 0 && (
                <div className="rounded-xl bg-ink-100/70 p-3 text-xs text-ink-700">
                  {card.vouchedBy.length > 0 && (
                    <div>
                      Vouched for by <b>{card.vouchedBy.join(', ')}</b>
                    </div>
                  )}
                </div>
              )}

              {card?.bio && <p className="text-sm text-ink-700">{card.bio}</p>}

              {card && card.services.length > 0 && (
                <div>
                  <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-ink-400">
                    Teaches or coaches
                  </div>
                  <ul className="space-y-1.5">
                    {card.services.map((s) => {
                      const items = summarizeJson(s.items)
                      const levels = summarizeJson(s.levels)
                      // Everything else the provider filled in for this one category - how, when
                      // and in what language they teach, their fee, and an institution's own
                      // answers (degree, positions, fee band) via detail. Blank fields just vanish
                      // from the join, same rule as everywhere else these are shown.
                      const extra = [
                        summarizeJson(s.modes),
                        summarizeJson(s.boards),
                        summarizeJson(s.classes),
                        summarizeJson(s.days),
                        summarizeJson(s.timings),
                        summarizeJson(s.teachingLanguages),
                        s.feeBand && `Budget - ${s.feeBand}`,
                        summarizeJson(s.detail),
                      ].filter(Boolean)
                      return (
                        <li key={s.id} className="text-sm">
                          <span className="font-semibold text-ink-900">
                            {NEED_CATEGORY_LABELS[s.category] ?? s.category}
                          </span>
                          {(items || levels) && (
                            <span className="text-ink-600">
                              {' '}— {[items, levels].filter(Boolean).join(' · ')}
                            </span>
                          )}
                          {extra.length > 0 && (
                            <p className="mt-0.5 text-xs text-ink-500">{extra.join(' · ')}</p>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex gap-3 text-sm">
                  <span className="mt-0.5 shrink-0 text-sun-600" aria-hidden="true"><MapPin size={15} /></span>
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-400">Mode</div>
                    <div className="text-ink-800">{modeLabel(card?.onlineOnly)}</div>
                  </div>
                </div>
                {card?.feeRangeText && (
                  <div className="flex gap-3 text-sm">
                    <span className="mt-0.5 shrink-0 text-sun-600" aria-hidden="true">₹</span>
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-400">Fee</div>
                      <div className="text-ink-800">{card.feeRangeText}</div>
                    </div>
                  </div>
                )}
              </div>

              {provider.averageRating != null && (
                <div className="flex items-center gap-2 text-xs text-ink-500">
                  <Stars rating={provider.averageRating} size={12} /> {provider.averageRating} from{' '}
                  {provider.ratingCount} connected {provider.ratingCount === 1 ? 'learner' : 'learners'}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-ink-200 p-4">
          {/* Why the last Connect failed, beside the button that was pressed: the page's own message
              sits behind this dialog, so without this a refusal looked like nothing happening. */}
          {connectError && (
            <div className="mb-3">
              <ErrorMessage message={connectError} />
            </div>
          )}
          {/* Low-key, above the Connect row: a safety tool, not something to press by mistake. Once
              blocked the card would show "not found" anyway, so the dialog closes. */}
          <div className="-mt-2 mb-2 flex justify-end">
            <ReportBlockActions participantId={provider.id} name={provider.fullName} onBlocked={onClose} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-ink-500">
              {connectBlockedReason ?? 'Contact details are shared only after a request is accepted.'}
            </span>
            {/* A connection that already exists shows its state where the button was, rather than
                a disabled button: there is nothing to press, and an existing connection is not a
                refusal. Only the never-requested case gets a button at all. */}
            {existingLabel ? (
              <span className="rounded-full bg-ink-100 px-3 py-1.5 text-sm font-semibold text-ink-600">
                {existingLabel}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onConnect(card)}
                disabled={connecting || Boolean(connectBlockedReason)}
                title={connectBlockedReason ?? undefined}
                className="btn-sun disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send size={15} /> {connecting ? 'Sending...' : 'Connect'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
